"use client"

import { useRef } from "react"
import Link from "next/link"
import {
  ArrowRight,
  Clock,
  CalendarDays,
  MessageSquareText,
  Users,
  UserCircle,
  FileText,
  Wallet,
  Smartphone,
  Bell,
  Shield,
  Fingerprint,
  CalendarClock,
  Palmtree,
  ClipboardList,
  Download,
  QrCode,
  AlertTriangle,
  Calculator,
  CheckCircle2,
  XCircle,
} from "lucide-react"
import FreeResourcesSection, {
  empleadosResources,
} from "@/components/public/free-resources-section"
import { Cabecera } from "@/components/landing/zindra/cabecera"
import { Pie } from "@/components/landing/zindra/pie"
import type { Idioma } from "@/lib/landing/idioma"

/** La misma página en los dos idiomas: /empleados y /en/team-app. */
export const RUTA_APP_EQUIPO = { es: "/empleados", en: "/en/team-app" } as const

const T = {
  es: {
    etiqueta: "Portal del Empleado",
    tituloA: "Un portal moderno",
    tituloB: "para tu equipo",
    intro:
      "Ofrece a tus empleados un espacio donde fichar, consultar turnos, solicitar vacaciones y acceder a sus nominas. Reduce consultas a RRHH y mejora la experiencia de tu equipo.",
    prueba: "Comenzar Prueba Gratuita",
    demo: "Solicitar Demo",
    contacto: "/contacto",
    empresas: "+500 empresas",
    confian: " confian en Zindra",
    fichajeRegistrado: "Fichaje Registrado",
    entradaHora: "Entrada - 08:57",
    ok: "OK",
    vacacionesAprobadas: "Vacaciones Aprobadas",
    fechasVacaciones: "15 - 22 Agosto",
    aprobado: "Aprobado",
    turnoManana: "Turno de Mañana",
    lunesViernes: "Lunes a Viernes",
    cifras: [
      { n: "-80%", x: "Consultas a RRHH" },
      { n: "100%", x: "Cumplimiento legal" },
      { n: "0", x: "Papel necesario" },
      { n: "24/7", x: "Autoservicio empleados" },
    ],
    problemaTitulo: "¿Cuanto tiempo pierdes en tareas administrativas?",
    problemaIntro:
      "Responder emails sobre nominas, gestionar vacaciones en papel, buscar documentos... Horas que podrias dedicar a hacer crecer tu negocio.",
    problemas: [
      {
        t: "Consultas repetitivas",
        x: "\"¿Me puedes enviar mi nomina?\" \"¿Cuantos dias de vacaciones tengo?\" Las mismas preguntas cada mes.",
      },
      {
        t: "Gestion manual",
        x: "Hojas de Excel, notas en papel, solicitudes por WhatsApp... Procesos que deberian estar automatizados.",
      },
      {
        t: "Cumplimiento legal",
        x: "La normativa de registro horario exige control. Sin un sistema, arriesgas sanciones de hasta 6.250€.",
      },
    ],
    todoTitulo: "Todo lo que tu equipo necesita",
    asistenciaTitulo: "Control de Asistencia",
    asistenciaTexto:
      "Tus empleados fichan en segundos con un clic, codigo QR o geolocalizacion. Cumples con la normativa de registro horario sin esfuerzo adicional.",
    asistenciaChips: ["Geolocalización", "Código QR", "App móvil"],
    fechaFichaje: "Lunes, 15 Enero",
    ficharEntrada: "Fichar Entrada",
    vacacionesTitulo: "Gestion de Vacaciones",
    vacacionesTexto:
      "Los empleados solicitan dias libres online. Tu apruebas o rechazas con un clic. Sin emails ni papeles.",
    vacacionesChips: ["Vacaciones", "Permisos", "Bajas"],
    saldo: "Saldo de vacaciones",
    diasTotales: "Días totales",
    veintidosDias: "22 días",
    usados: "10 usados",
    disponibles: "12 disponibles",
    estaSemana: "Esta semana",
    lunViernes: "Lun - Vie: Mañana",
    sabDom: "Sáb - Dom: Libre",
    semanales: "semanales",
    turnosTitulo: "Turnos y Horarios",
    turnosTexto: "Tus empleados consultan sus turnos sin preguntarte. Publicas la planificacion y ellos la ven al instante.",
    tuSugerencia: "Tu sugerencia",
    enviada: "Enviada hace 2 días",
    respondida: "Respondida",
    buzonTitulo: "Buzon de Sugerencias",
    buzonTexto: "Recibe feedback de tu equipo de forma confidencial. Detecta problemas antes de que escalen.",
    mesCalendario: "Enero 2024",
    diasSemana: ["L", "M", "X", "J", "V", "S", "D"],
    calendarioTitulo: "Calendario de Equipo",
    calendarioTexto: "Tu equipo ve quien esta de vacaciones o ausente. Menos preguntas, mejor coordinacion.",
    mesNomina: "Diciembre 2024",
    neto: "Neto a percibir",
    bruto: "Bruto: €2,400",
    nominasTitulo: "Nominas",
    nominasTexto: "Sube las nominas una vez. Cada empleado accede a las suyas sin pedirtelas cada mes.",
    misDocumentos: "Mis documentos",
    ficheros: ["Contrato_2024.pdf", "Certificado_IRPF.pdf", "Manual_empleado.pdf"],
    documentosTitulo: "Documentos",
    documentosTexto: "Comparte contratos, manuales y certificados. Tus empleados los tienen siempre a mano.",
    appEtiqueta: "App Movil",
    appTituloA: "Tus empleados lo llevan",
    appTituloB: "en el bolsillo.",
    appTexto:
      "App nativa para iOS y Android. Tus empleados fichan, consultan turnos y gestionan sus solicitudes desde el movil. Tu reduces llamadas y emails.",
    appPuntos: [
      { t: "Fichaje con QR", x: "Genera codigos QR para cada centro de trabajo" },
      { t: "Notificaciones push", x: "Tus empleados reciben alertas de turnos y aprobaciones" },
      { t: "100% Seguro", x: "Autenticacion biometrica y datos cifrados" },
    ],
    buenosDias: "Buenos días",
    fichadoHace: "Fichado hace 15 min",
    ficharSalida: "Fichar Salida",
    vacaciones: "Vacaciones",
    doceDias: "12 días",
    esteMes: "Este mes",
    beneficiosEtiqueta: "Beneficios para tu negocio",
    beneficiosTitulo: "Menos gestion, mas productividad",
    beneficiosIntro: "Automatiza tareas administrativas y libera tiempo para lo importante.",
    beneficios: [
      { t: "Datos centralizados", x: "Cada empleado tiene su perfil con datos actualizados. No mas buscar informacion en diferentes sitios." },
      { t: "Historial completo", x: "Accede al registro de fichajes, vacaciones y documentos de cualquier empleado cuando lo necesites." },
      {
        t: "Comunicacion automatica",
        x: "Tus empleados reciben notificaciones de aprobaciones y cambios de turno sin que tengas que avisar manualmente.",
      },
    ],
    compararTitulo: "Con Zindra vs Sin Zindra",
    compararIntro: "Compara como cambia la gestion de tu equipo cuando automatizas los procesos.",
    sinZindra: "Sin Zindra",
    sin: [
      "Empleados preguntando por sus nominas cada mes",
      "Vacaciones gestionadas por email o WhatsApp",
      "Fichajes en papel o sin ningun control",
      "Documentos dispersos en diferentes carpetas",
      "Turnos comunicados por mensaje individual",
      "Historial de empleados perdido al cambiar de gestor",
      "Riesgo de sanciones por incumplimiento de registro horario",
      "Horas semanales dedicadas a tareas administrativas",
    ],
    conZindra: "Con Zindra",
    con: [
      "Nominas accesibles desde el portal del empleado",
      "Solicitudes de vacaciones online con aprobacion en un clic",
      "Fichaje digital con geolocalizacion y codigo QR",
      "Todos los documentos centralizados y accesibles",
      "Turnos publicados que todos ven al instante",
      "Historial completo de cada empleado siempre disponible",
      "Cumplimiento legal garantizado con el registro horario",
      "Autoservicio para empleados, cero preguntas repetitivas",
    ],
    recursosTitulo: "Recursos gratuitos de gestión de personal",
    recursosSubtitulo: "Plantillas, calculadoras y guías para optimizar tu equipo.",
    ctaTitulo: "¿Quieres ofrecer esto a tus empleados?",
    ctaTexto: "Mejora la experiencia de tu equipo con un portal de empleado moderno y fácil de usar.",
    ventas: "Hablar con Ventas",
  },
  en: {
    etiqueta: "Team app",
    tituloA: "A modern app",
    tituloB: "for your team",
    intro:
      "Give your staff one place to clock in, check their rota, request holidays and download their payslips. Fewer questions for HR, and a better day-to-day for your team.",
    prueba: "Start free trial",
    demo: "Book a demo",
    contacto: "/en/contact",
    empresas: "500+ businesses",
    confian: " trust Zindra",
    fichajeRegistrado: "Clock-in recorded",
    entradaHora: "In - 08:57",
    ok: "OK",
    vacacionesAprobadas: "Holiday approved",
    fechasVacaciones: "15 - 22 August",
    aprobado: "Approved",
    turnoManana: "Morning shift",
    lunesViernes: "Monday to Friday",
    cifras: [
      { n: "-80%", x: "Questions to HR" },
      { n: "100%", x: "Legal compliance" },
      { n: "0", x: "Paper needed" },
      { n: "24/7", x: "Staff self-service" },
    ],
    problemaTitulo: "How much time do you lose to admin?",
    problemaIntro:
      "Answering emails about payslips, tracking holidays on paper, hunting for documents... Hours you could spend growing your business.",
    problemas: [
      {
        t: "The same questions",
        x: "\"Can you send me my payslip?\" \"How many days off do I have left?\" The same questions every month.",
      },
      {
        t: "Manual admin",
        x: "Spreadsheets, notes on paper, requests over WhatsApp... Processes that should run on their own.",
      },
      {
        t: "Legal compliance",
        x: "Spanish law requires every employer to keep a daily record of working hours. Without a system, you risk fines of up to €6,250.",
      },
    ],
    todoTitulo: "Everything your team needs",
    asistenciaTitulo: "Time and attendance",
    asistenciaTexto:
      "Staff clock in in seconds with one tap, a QR code or geolocation. You meet Spain's working-hours recording rules with no extra effort.",
    asistenciaChips: ["Geolocation", "QR code", "Mobile app"],
    fechaFichaje: "Monday, 15 January",
    ficharEntrada: "Clock in",
    vacacionesTitulo: "Holiday management",
    vacacionesTexto: "Staff request time off online. You approve or decline with one click. No emails, no paperwork.",
    vacacionesChips: ["Holidays", "Leave", "Sick leave"],
    saldo: "Holiday balance",
    diasTotales: "Total days",
    veintidosDias: "22 days",
    usados: "10 taken",
    disponibles: "12 left",
    estaSemana: "This week",
    lunViernes: "Mon - Fri: Morning",
    sabDom: "Sat - Sun: Off",
    semanales: "a week",
    turnosTitulo: "Rotas and schedules",
    turnosTexto: "Staff check their shifts without having to ask you. You publish the rota and they see it straight away.",
    tuSugerencia: "Your suggestion",
    enviada: "Sent 2 days ago",
    respondida: "Answered",
    buzonTitulo: "Suggestion box",
    buzonTexto: "Get confidential feedback from your team. Spot problems before they escalate.",
    mesCalendario: "January 2024",
    diasSemana: ["M", "T", "W", "T", "F", "S", "S"],
    calendarioTitulo: "Team calendar",
    calendarioTexto: "Your team can see who is on holiday or off. Fewer questions, better coordination.",
    mesNomina: "December 2024",
    neto: "Net pay",
    bruto: "Gross: €2,400",
    nominasTitulo: "Payslips",
    nominasTexto: "Upload payslips once. Each employee gets their own without asking you every month.",
    misDocumentos: "My documents",
    ficheros: ["Contract_2024.pdf", "Tax_certificate.pdf", "Staff_handbook.pdf"],
    documentosTitulo: "Documents",
    documentosTexto: "Share contracts, handbooks and certificates. Your staff always have them to hand.",
    appEtiqueta: "Mobile app",
    appTituloA: "Your staff carry it",
    appTituloB: "in their pocket.",
    appTexto:
      "A native app for iOS and Android. Staff clock in, check their shifts and manage their requests from their phone. You get fewer calls and emails.",
    appPuntos: [
      { t: "QR clock-in", x: "Generate a QR code for each workplace" },
      { t: "Push notifications", x: "Staff get alerts about shifts and approvals" },
      { t: "Fully secure", x: "Biometric sign-in and encrypted data" },
    ],
    buenosDias: "Good morning",
    fichadoHace: "Clocked in 15 min ago",
    ficharSalida: "Clock out",
    vacaciones: "Holidays",
    doceDias: "12 days",
    esteMes: "This month",
    beneficiosEtiqueta: "Benefits for your business",
    beneficiosTitulo: "Less admin, more productivity",
    beneficiosIntro: "Automate admin tasks and free up time for what matters.",
    beneficios: [
      { t: "Everything in one place", x: "Each employee has a profile with up-to-date details. No more searching for information in different places." },
      { t: "Full history", x: "See any employee's clock-ins, holidays and documents whenever you need them." },
      {
        t: "Automatic updates",
        x: "Staff are notified of approvals and shift changes without you having to tell each of them.",
      },
    ],
    compararTitulo: "With Zindra vs without Zindra",
    compararIntro: "See how managing your team changes once the processes run themselves.",
    sinZindra: "Without Zindra",
    sin: [
      "Staff asking for their payslips every month",
      "Holidays handled over email or WhatsApp",
      "Clock-ins on paper, or not recorded at all",
      "Documents scattered across different folders",
      "Shifts sent out one message at a time",
      "Staff history lost when a manager leaves",
      "Risk of fines for not recording working hours",
      "Hours every week spent on admin",
    ],
    conZindra: "With Zindra",
    con: [
      "Payslips available in the team app",
      "Online holiday requests, approved in one click",
      "Digital clock-in with geolocation and QR codes",
      "All documents in one place, easy to find",
      "Published rotas everyone sees instantly",
      "Each employee's full history, always available",
      "Working-hours recording that keeps you compliant",
      "Staff self-service, no more repeated questions",
    ],
    recursosTitulo: "",
    recursosSubtitulo: "",
    ctaTitulo: "Want to give this to your team?",
    ctaTexto: "Make work easier for your staff with a modern, easy-to-use team app.",
    ventas: "Talk to sales",
  },
}

// Spotlight card handler
function SpotlightCard({
  children,
  className = "",
}: {
  children: React.ReactNode
  className?: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    card.style.setProperty("--mouse-x", `${x}px`)
    card.style.setProperty("--mouse-y", `${y}px`)
  }

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      className={`spotlight-card ${className}`}
    >
      {children}
    </div>
  )
}

const ICONOS_PROBLEMA = [
  <AlertTriangle key="a" className="w-10 h-10 text-amber-400 mb-4" />,
  <Calculator key="c" className="w-10 h-10 text-red-400 mb-4" />,
  <Clock key="r" className="w-10 h-10 text-sky-400 mb-4" />,
]

const ICONOS_APP = [QrCode, Bell, Shield]

const ICONOS_BENEFICIO = [
  { Icono: UserCircle, clase: "bg-white text-zinc-900 shadow-sm border border-zinc-100" },
  { Icono: ClipboardList, clase: "border bg-emerald-50 text-emerald-600 border-emerald-100" },
  { Icono: Bell, clase: "bg-white text-zinc-900 shadow-sm border border-zinc-100" },
]

export function EmployeesLandingPage({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const otro = idioma === "es" ? "en" : "es"
  return (
      <div className="min-h-screen overflow-x-hidden text-zinc-900 bg-zinc-50 relative" lang={idioma}>
        {/* Grid lines background */}
        <div className="fixed grid-lines w-full h-screen top-0 right-0 left-0 pointer-events-none z-0" />

      <Cabecera fija idioma={idioma} otroIdioma={RUTA_APP_EQUIPO[otro]} />

      <div className="relative z-10 flex flex-col min-h-screen pt-16 lg:pt-20">
        <main className="flex-1 w-full">
          {/* Hero Section */}
          <section className="px-6 lg:px-12 pt-12 lg:pt-20 pb-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 min-h-[60vh] lg:min-h-[75vh] max-w-7xl mx-auto items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium bg-white border-zinc-200 text-zinc-600">
                <span className="w-2 h-2 rounded-full animate-pulse bg-emerald-500" />
                {t.etiqueta}
              </div>

              <h1 className="leading-none text-4xl lg:text-6xl xl:text-7xl font-medium text-zinc-900 tracking-tight">
                {t.tituloA}{" "}
                <span className="relative inline-block text-emerald-600">
                  {t.tituloB}
                  <svg
                    className="absolute w-full h-3 -bottom-1 left-0 -z-10 text-emerald-200"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      opacity="0.6"
                    />
                  </svg>
                </span>
              </h1>

              <p className="leading-relaxed text-lg lg:text-xl font-normal text-zinc-500 max-w-2xl">
                {t.intro}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  href="/register"
                  className="text-white transition-all font-medium px-8 py-4 rounded-xl text-base flex items-center gap-2 justify-center group shadow-lg bg-primary hover:bg-[var(--primary-hover)] shadow-zinc-900/20"
                >
                  {t.prueba}
                  <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  href={t.contacto}
                  className="bg-white text-zinc-900 border border-zinc-200 hover:border-zinc-300 transition-all font-medium px-8 py-4 rounded-xl text-base flex items-center gap-2 justify-center"
                >
                  {t.demo}
                </Link>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  <div className="w-10 h-10 rounded-full border-2 border-white bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-white bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-white bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <Bell className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-sm text-zinc-600">
                  <span className="font-semibold text-zinc-900">{t.empresas}</span>{t.confian}
                </div>
              </div>
            </div>

            {/* Floating Cards */}
            <div className="lg:col-span-5 flex flex-col lg:mt-0 h-full mt-8 relative justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 opacity-20 blur-3xl rounded-full pointer-events-none bg-emerald-400" />
              <div className="relative space-y-4">
                <div
                  className="glass-panel flex gap-4 animate-float w-full max-w-sm rounded-xl mx-auto p-4 shadow-lg items-center"
                  style={{ animationDelay: "0s" }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center border shrink-0 bg-emerald-50 text-emerald-600 border-emerald-100">
                    <Fingerprint className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-zinc-900">{t.fichajeRegistrado}</p>
                    <p className="text-xs text-zinc-500 truncate">{t.entradaHora}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded border font-medium bg-emerald-50 text-emerald-600 border-emerald-200">
                    {t.ok}
                  </span>
                </div>

                <div
                  className="glass-panel p-4 rounded-xl flex items-center gap-4 shadow-lg lg:ml-8 max-w-sm mx-auto w-full animate-float"
                  style={{ animationDelay: "1.5s" }}
                >
                  <div className="flex shrink-0 w-10 h-10 border rounded-full items-center justify-center text-white bg-amber-500 border-amber-600">
                    <Palmtree className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-zinc-900">{t.vacacionesAprobadas}</p>
                    <p className="text-xs text-zinc-500 truncate">{t.fechasVacaciones}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded border font-medium bg-emerald-100 border-emerald-200 text-emerald-700">
                    {t.aprobado}
                  </span>
                </div>

                <div
                  className="glass-panel p-4 rounded-xl flex items-center gap-4 shadow-lg lg:-ml-4 max-w-sm mx-auto w-full animate-float"
                  style={{ animationDelay: "2.5s" }}
                >
                  <div className="w-10 h-10 rounded-full text-sky-600 flex items-center justify-center border shrink-0 bg-sky-50 border-sky-100">
                    <CalendarClock className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-zinc-900">{t.turnoManana}</p>
                    <p className="text-xs text-zinc-500 truncate">{t.lunesViernes}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded border font-medium bg-zinc-900 text-white border-zinc-700">
                    08:00-16:00
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* White background container */}
          <div className="bg-white w-full z-20 rounded-t-3xl pt-12 pb-12 relative shadow-sm">
            {/* Stats Section */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 w-full mb-16 lg:mb-24">
              <div className="grid grid-cols-2 lg:grid-cols-4 p-6 lg:p-8 bg-zinc-50 border-zinc-200 border rounded-2xl gap-6 lg:gap-8">
                {t.cifras.map((c) => (
                  <div key={c.n} className="space-y-1 text-center lg:text-left">
                    <h3 className="text-3xl lg:text-4xl font-semibold tracking-tight text-zinc-900">{c.n}</h3>
                    <p className="text-sm font-medium text-zinc-500">{c.x}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Problem Statement - Dark Block */}
          <section className="px-6 lg:px-12 py-16 lg:py-20 bg-zinc-900 text-white">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-medium tracking-tight mb-6">
                  {t.problemaTitulo}
                </h2>
                <p className="text-lg text-zinc-400">
                  {t.problemaIntro}
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {t.problemas.map((p, i) => (
                  <div key={p.t} className="p-6 rounded-2xl bg-zinc-800/50 border border-zinc-700">
                    {ICONOS_PROBLEMA[i]}
                    <h3 className="text-xl font-semibold mb-3">{p.t}</h3>
                    <p className="text-zinc-400">
                      {p.x}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* White background container */}
          <div className="bg-white w-full z-20 rounded-t-3xl pt-12 pb-12 relative shadow-sm -mt-6">
            {/* Main Features Section - Bento Grid */}
            <section id="funcionalidades" className="px-6 lg:px-12 mb-16 lg:mb-24 max-w-7xl mx-auto">
              <h2 className="text-3xl lg:text-5xl font-medium text-zinc-900 tracking-tight mb-10 lg:mb-12">
                {t.todoTitulo}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 auto-rows-fr">
                {/* Fichaje - Large Card */}
                <div className="row-span-1 lg:row-span-2 group overflow-hidden lg:p-10 flex flex-col min-h-[500px] lg:min-h-full transition-transform hover:scale-[1.01] duration-300 border rounded-3xl p-6 relative justify-between bg-emerald-600 border-emerald-600">
                  <div className="z-20 mt-auto relative">
                    <div className="flex text-white bg-white/10 w-12 h-12 border-white/10 border rounded-2xl mb-6 backdrop-blur-md items-center justify-center">
                      <Fingerprint className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-white tracking-tight mb-3">{t.asistenciaTitulo}</h3>
                    <p className="mb-6 text-sm lg:text-base leading-relaxed max-w-sm text-emerald-100">
                      {t.asistenciaTexto}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {t.asistenciaChips.map((c) => (
                        <span key={c} className="text-xs px-3 py-1 rounded-full bg-white/10 text-white border border-white/20">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 flex w-full mt-8 mb-8 relative items-center justify-center">
                    <div className="transform group-hover:-translate-y-2 transition-transform duration-500 bg-white w-full max-w-[280px] z-10 rounded-xl p-5 relative shadow-2xl">
                      <div className="flex gap-2 border-zinc-100 border-b mb-4 pb-3 items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                      </div>
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                          <Clock className="w-8 h-8 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-zinc-900">08:57</p>
                          <p className="text-xs text-zinc-500">{t.fechaFichaje}</p>
                        </div>
                        <button className="w-full text-white text-sm py-3 rounded-lg font-medium bg-primary">
                          {t.ficharEntrada}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vacaciones - Wide Card */}
                <div className="col-span-1 md:col-span-2 group overflow-hidden lg:p-10 min-h-[400px] flex flex-col md:flex-row transition-transform hover:scale-[1.01] duration-300 bg-amber-100 border-amber-200 border rounded-3xl p-6 relative items-center justify-between">
                  <div className="relative z-20 flex flex-col h-full justify-between w-full md:w-1/2 mb-8 md:mb-0">
                    <div>
                      <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                        <Palmtree className="w-6 h-6" />
                      </div>
                      <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.vacacionesTitulo}</h3>
                      <p className="text-zinc-700 text-sm lg:text-base mb-6 leading-relaxed max-w-xs">
                        {t.vacacionesTexto}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {t.vacacionesChips.map((c) => (
                        <span key={c} className="text-xs px-3 py-1 rounded-full bg-white/60 text-zinc-700">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="relative w-full md:w-1/2 h-full min-h-[220px] flex items-center justify-end">
                    <div className="absolute right-0 md:right-[-40px] w-full md:w-[110%] group-hover:scale-105 transition-all duration-500 ease-out">
                      <div className="bg-white/90 backdrop-blur rounded-xl shadow-lg border border-amber-200/50 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-medium text-zinc-900">{t.saldo}</span>
                          <span className="text-xs text-amber-600 font-medium">2024</span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-600">{t.diasTotales}</span>
                            <span className="text-sm font-semibold text-zinc-900">{t.veintidosDias}</span>
                          </div>
                          <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: "45%" }} />
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-emerald-600">{t.usados}</span>
                            <span className="text-amber-600 font-medium">{t.disponibles}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Turnos */}
                <div className="group relative overflow-hidden bg-sky-50 rounded-3xl p-6 lg:p-10 flex flex-col justify-between min-h-[350px] transition-transform hover:scale-[1.01] duration-300 border border-sky-100">
                  <div className="relative w-full h-32 mb-4">
                    <div className="absolute right-0 top-0 w-full max-w-[200px]">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-sky-100 transform group-hover:scale-105 transition-transform">
                        <div className="text-xs text-zinc-500 mb-2">{t.estaSemana}</div>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="text-xs text-zinc-700">{t.lunViernes}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-zinc-300" />
                            <span className="text-xs text-zinc-400">{t.sabDom}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-2 border-t border-zinc-100">
                          <span className="text-lg font-semibold text-zinc-900">40h</span>
                          <span className="text-xs text-zinc-500 ml-1">{t.semanales}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto relative z-20">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                      <CalendarClock className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.turnosTitulo}</h3>
                    <p className="text-zinc-700 text-sm lg:text-base mb-4 leading-relaxed">{t.turnosTexto}</p>
                  </div>
                </div>

                {/* Buzón de Sugerencias */}
                <div className="group relative overflow-hidden bg-violet-50 rounded-3xl p-6 lg:p-10 flex flex-col justify-between min-h-[350px] transition-transform hover:scale-[1.01] duration-300 border border-violet-100">
                  <div className="relative w-full h-32 mb-4">
                    <div className="absolute right-0 top-0 w-full max-w-[200px]">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-violet-100 transform group-hover:scale-105 transition-transform">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                            <MessageSquareText className="w-4 h-4 text-violet-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-zinc-900">{t.tuSugerencia}</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5">{t.enviada}</p>
                            <div className="mt-2 flex items-center gap-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                              <span className="text-[10px] text-emerald-600">{t.respondida}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto relative z-20">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                      <MessageSquareText className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.buzonTitulo}</h3>
                    <p className="text-zinc-700 text-sm lg:text-base mb-4 leading-relaxed">{t.buzonTexto}</p>
                  </div>
                </div>

                {/* Calendario de Equipo */}
                <div className="group relative overflow-hidden bg-rose-50 rounded-3xl p-6 lg:p-10 flex flex-col justify-between min-h-[350px] transition-transform hover:scale-[1.01] duration-300 border border-rose-100">
                  <div className="relative w-full h-32 mb-4">
                    <div className="absolute right-0 top-0 w-full max-w-[200px]">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-rose-100 transform group-hover:scale-105 transition-transform">
                        <div className="text-xs text-zinc-500 mb-2">{t.mesCalendario}</div>
                        <div className="grid grid-cols-7 gap-1 text-center text-[8px] text-zinc-400 mb-1">
                          {t.diasSemana.map((d, i) => (
                            <span key={i}>{d}</span>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-[9px]">
                          <span className="text-zinc-300">29</span>
                          <span className="text-zinc-300">30</span>
                          <span className="text-zinc-300">31</span>
                          <span>1</span>
                          <span>2</span>
                          <span>3</span>
                          <span>4</span>
                          <span className="bg-amber-100 text-amber-700 rounded">5</span>
                          <span className="bg-amber-100 text-amber-700 rounded">6</span>
                          <span className="bg-amber-100 text-amber-700 rounded">7</span>
                          <span>8</span>
                          <span>9</span>
                          <span>10</span>
                          <span>11</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto relative z-20">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                      <Users className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.calendarioTitulo}</h3>
                    <p className="text-zinc-700 text-sm lg:text-base mb-4 leading-relaxed">{t.calendarioTexto}</p>
                  </div>
                </div>

                {/* Nóminas */}
                <div className="group relative overflow-hidden bg-lime-50 rounded-3xl p-6 lg:p-10 flex flex-col justify-between min-h-[350px] transition-transform hover:scale-[1.01] duration-300 border border-lime-100">
                  <div className="relative w-full h-32 mb-4">
                    <div className="absolute right-0 top-0 w-full max-w-[200px]">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-lime-100 transform group-hover:scale-105 transition-transform">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-zinc-500">{t.mesNomina}</span>
                          <Download className="w-3.5 h-3.5 text-zinc-400" />
                        </div>
                        <div className="text-xl font-semibold text-zinc-900">€1,845.50</div>
                        <div className="text-[10px] text-zinc-400 mt-1">{t.neto}</div>
                        <div className="mt-2 pt-2 border-t border-zinc-100 flex justify-between text-[10px]">
                          <span className="text-zinc-500">{t.bruto}</span>
                          <span className="text-red-500">-€554.50</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto relative z-20">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                      <Wallet className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.nominasTitulo}</h3>
                    <p className="text-zinc-700 text-sm lg:text-base mb-4 leading-relaxed">{t.nominasTexto}</p>
                  </div>
                </div>

                {/* Documentos */}
                <div className="group relative overflow-hidden bg-cyan-50 rounded-3xl p-6 lg:p-10 flex flex-col justify-between min-h-[350px] transition-transform hover:scale-[1.01] duration-300 border border-cyan-100">
                  <div className="relative w-full h-32 mb-4">
                    <div className="absolute right-0 top-0 w-full max-w-[200px]">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-cyan-100 transform group-hover:scale-105 transition-transform">
                        <div className="text-xs text-zinc-500 mb-2">{t.misDocumentos}</div>
                        <div className="space-y-2">
                          {t.ficheros.map((f) => (
                            <div key={f} className="flex items-center gap-2 text-xs">
                              <FileText className="w-3.5 h-3.5 text-cyan-600" />
                              <span className="text-zinc-700 truncate">{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto relative z-20">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mb-6 text-zinc-900">
                      <FileText className="w-6 h-6" />
                    </div>
                    <h3 className="lg:text-3xl text-2xl font-semibold text-zinc-900 tracking-tight mb-3">{t.documentosTitulo}</h3>
                    <p className="text-zinc-700 text-sm lg:text-base mb-4 leading-relaxed">{t.documentosTexto}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* App Mobile Section */}
            <section className="py-12 lg:py-20 max-w-7xl mx-auto px-6 lg:px-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <span className="font-medium text-xs tracking-widest uppercase mb-3 block w-fit px-3 py-1.5 rounded text-emerald-600 bg-emerald-50">
                    {t.appEtiqueta}
                  </span>
                  <h2 className="text-3xl lg:text-5xl font-medium text-zinc-900 tracking-tight leading-tight">
                    {t.appTituloA}{" "}
                    <span className="text-zinc-400">{t.appTituloB}</span>
                  </h2>
                  <p className="leading-relaxed text-lg text-zinc-600 max-w-xl">
                    {t.appTexto}
                  </p>
                  <div className="space-y-4">
                    {t.appPuntos.map((p, i) => {
                      const Icono = ICONOS_APP[i]
                      return (
                        <div key={p.t} className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                            <Icono className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium text-zinc-900">{p.t}</h4>
                            <p className="text-sm text-zinc-500">{p.x}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button className="bg-zinc-900 text-white px-6 py-3 rounded-xl flex items-center gap-2 text-sm font-medium hover:bg-zinc-800 transition-colors">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      App Store
                    </button>
                    <button className="bg-zinc-900 text-white px-6 py-3 rounded-xl flex items-center gap-2 text-sm font-medium hover:bg-zinc-800 transition-colors">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                      </svg>
                      Google Play
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 opacity-20 blur-3xl rounded-full pointer-events-none bg-emerald-400" />
                  <div className="relative bg-zinc-900 rounded-[3rem] p-3 max-w-[300px] mx-auto shadow-2xl">
                    <div className="bg-zinc-950 rounded-[2.5rem] overflow-hidden">
                      <div className="h-6 flex items-center justify-center">
                        <div className="w-20 h-4 bg-zinc-900 rounded-full" />
                      </div>
                      <div className="p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="text-white">
                            <p className="text-xs text-zinc-400">{t.buenosDias}</p>
                            <p className="font-medium">María García</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                            MG
                          </div>
                        </div>
                        <div className="bg-zinc-800 rounded-2xl p-4 text-center">
                          <Clock className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-white">09:15</p>
                          <p className="text-xs text-zinc-400 mt-1">{t.fichadoHace}</p>
                          <button className="w-full mt-4 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium">
                            {t.ficharSalida}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-zinc-800 rounded-xl p-3">
                            <CalendarDays className="w-5 h-5 text-amber-400 mb-2" />
                            <p className="text-xs text-zinc-400">{t.vacaciones}</p>
                            <p className="text-white font-medium">{t.doceDias}</p>
                          </div>
                          <div className="bg-zinc-800 rounded-xl p-3">
                            <CalendarClock className="w-5 h-5 text-sky-400 mb-2" />
                            <p className="text-xs text-zinc-400">{t.esteMes}</p>
                            <p className="text-white font-medium">152h</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Feature List Section */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 relative mb-16 lg:mb-20">
              <div className="flex flex-col lg:flex-row mb-12 items-start lg:items-end justify-between gap-6">
                <div className="max-w-2xl">
                  <span className="font-medium text-xs tracking-widest uppercase mb-3 block w-fit px-3 py-1.5 rounded text-emerald-600 bg-emerald-50">
                    {t.beneficiosEtiqueta}
                  </span>
                  <h2 className="text-3xl lg:text-5xl font-medium text-zinc-900 tracking-tight">{t.beneficiosTitulo}</h2>
                </div>
                <p className="max-w-md text-zinc-500 text-sm lg:text-base lg:text-right">
                  {t.beneficiosIntro}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {t.beneficios.map((b, i) => {
                  const { Icono, clase } = ICONOS_BENEFICIO[i]
                  return (
                    <SpotlightCard
                      key={b.t}
                      className="group p-8 border rounded-2xl hover:shadow-xl transition-all duration-300 flex flex-col h-full bg-zinc-50 border-zinc-200 hover:border-zinc-300"
                    >
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 ${clase}`}>
                        <Icono className="w-7 h-7" />
                      </div>
                      <h3 className="text-xl font-semibold tracking-tight mb-3 text-zinc-900">{b.t}</h3>
                      <p className="text-zinc-500 leading-relaxed text-sm lg:text-base">
                        {b.x}
                      </p>
                    </SpotlightCard>
                  )
                })}
              </div>
            </section>

            {/* Comparison Section */}
            <section className="px-6 lg:px-12 py-16 lg:py-24 max-w-7xl mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl lg:text-4xl font-medium text-zinc-900 tracking-tight mb-6">
                  {t.compararTitulo}
                </h2>
                <p className="text-lg text-zinc-600">
                  {t.compararIntro}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Without Zindra */}
                <div className="rounded-2xl border-2 border-red-200 bg-red-50/50 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <XCircle className="w-8 h-8 text-red-500" />
                    <h3 className="text-xl font-semibold text-red-900">{t.sinZindra}</h3>
                  </div>
                  <ul className="space-y-4">
                    {t.sin.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-red-800">
                        <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* With Zindra */}
                <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    <h3 className="text-xl font-semibold text-emerald-900">{t.conZindra}</h3>
                  </div>
                  <ul className="space-y-4">
                    {t.con.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-emerald-800">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-500" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Free Resources Section: sus recursos (glosario, plantillas…) son en castellano. */}
            {idioma === "es" && (
              <FreeResourcesSection
                resources={empleadosResources}
                title={t.recursosTitulo}
                subtitle={t.recursosSubtitulo}
              />
            )}

            {/* CTA Section */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 my-16 lg:my-24">
              <div className="lg:p-20 overflow-hidden text-center rounded-3xl p-8 relative bg-primary">
                <div className="relative z-10 max-w-3xl mx-auto space-y-8">
                  <h2 className="text-3xl lg:text-5xl leading-tight font-medium text-white tracking-tight">
                    {t.ctaTitulo}
                  </h2>
                  <p className="text-lg lg:text-xl max-w-2xl mx-auto text-emerald-100">
                    {t.ctaTexto}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Link
                      href="/register"
                      className="font-medium px-8 py-4 rounded-xl transition-transform hover:scale-105 shadow-xl bg-white hover:bg-zinc-50 w-full sm:w-auto text-center justify-center flex text-emerald-600"
                    >
                      {t.prueba}
                    </Link>
                    <Link
                      href={t.contacto}
                      className="backdrop-blur border font-medium px-8 py-4 rounded-xl transition-colors bg-white/10 text-white border-white/20 hover:bg-white/20 w-full sm:w-auto text-center justify-center flex"
                    >
                      {t.ventas}
                    </Link>
                  </div>
                </div>
                <div
                  className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none"
                  style={{
                    backgroundImage: "radial-gradient(circle at 10% 20%, rgba(255,255,255,0.4) 0%, transparent 20%), radial-gradient(circle at 90% 80%, rgba(255,255,255,0.4) 0%, transparent 20%)",
                  }}
                />
              </div>
            </section>

          </div>
        </main>
      </div>

      <Pie idioma={idioma} />
    </div>
  )
}
